import { makeAutoObservable, runInAction } from 'mobx';
import services from '../services';
import {
    generateLoadEntity,
    generateLoadList,
} from '../utils/mobx';
import { extractErrorMessage } from '../utils/helpers';

class AwarePhishStore {
    summary = null;
    campaigns = [];
    campaign = null;
    config = null;
    templates = [];
    sendingProfiles = [];
    delivery = null;
    deliveryProfiles = [];

    loadingSummary = false;
    loadingCampaigns = false;
    loadingCampaign = false;
    loadingTemplates = false;
    loadingProfiles = false;
    loadingDelivery = false;
    loadingDeliveryProfiles = false;
    creatingCampaign = false;
    savingAsset = false;
    generatingTemplate = false;
    error = null;

    constructor() {
        makeAutoObservable(this);
    }

    setError(error) {
        this.error = error;
    }

    loadConfig = generateLoadEntity(
        'loadConfig',
        this,
        'loadingSummary',
        async (companyId) => {
            return services.Companies.awarePhishService(companyId).config();
        },
        'config'
    );

    loadSummary = generateLoadEntity(
        'loadSummary',
        this,
        'loadingSummary',
        async (companyId) => {
            return services.Companies.awarePhishService(companyId).summary();
        },
        'summary'
    );

    loadCampaigns = generateLoadList(
        'loadCampaigns',
        this,
        'loadingCampaigns',
        async (companyId) => {
            const result = await services.Companies.awarePhishService(
                companyId
            ).listCampaigns();
            return result.campaigns || [];
        },
        'campaigns'
    );

    loadCampaign = generateLoadEntity(
        'loadCampaign',
        this,
        'loadingCampaign',
        async (companyId, campaignId) => {
            return services.Companies.awarePhishService(companyId).getCampaign(
                campaignId
            );
        },
        'campaign'
    );

    loadTemplates = generateLoadList(
        'loadTemplates',
        this,
        'loadingTemplates',
        async (companyId) => {
            const result = await services.Companies.awarePhishService(
                companyId
            ).listTemplates();
            return result.templates || [];
        },
        'templates'
    );

    loadSendingProfiles = generateLoadList(
        'loadSendingProfiles',
        this,
        'loadingProfiles',
        async (companyId) => {
            const result = await services.Companies.awarePhishService(
                companyId
            ).listSendingProfiles();
            return result.profiles || [];
        },
        'sendingProfiles'
    );

    async createCampaign(companyId, payload) {
        this.creatingCampaign = true;
        this.error = null;
        try {
            const c = await services.Companies.awarePhishService(
                companyId
            ).createCampaign(payload);
            runInAction(() => {
                this.campaign = c;
                this.campaigns = [c, ...this.campaigns];
            });
            return c;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.creatingCampaign = false;
            });
        }
    }

    async saveTemplate(companyId, templateId, payload) {
        this.savingAsset = true;
        this.error = null;
        try {
            const svc = services.Companies.awarePhishService(companyId);
            const t = templateId
                ? await svc.updateTemplate(templateId, payload)
                : await svc.createTemplate(payload);
            await this.loadTemplates(companyId);
            return t;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async loadDelivery(companyId) {
        this.loadingDelivery = true;
        this.error = null;
        try {
            const delivery = await services.Companies.awarePhishService(
                companyId
            ).getDelivery();
            runInAction(() => {
                this.delivery = delivery;
            });
            return delivery;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.loadingDelivery = false;
            });
        }
    }

    async saveDelivery(companyId, payload) {
        this.savingAsset = true;
        this.error = null;
        try {
            const delivery = await services.Companies.awarePhishService(
                companyId
            ).updateDelivery(payload);
            runInAction(() => {
                this.delivery = delivery;
            });
            return delivery;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async syncReports(companyId) {
        this.savingAsset = true;
        this.error = null;
        try {
            return await services.Companies.awarePhishService(
                companyId
            ).syncReports();
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
            try {
                await this.loadDelivery(companyId);
            } catch (e) {
                /* ignore */
            }
        }
    }

    async sendDeliveryTest(companyId, payload) {
        this.savingAsset = true;
        this.error = null;
        try {
            return await services.Companies.awarePhishService(
                companyId
            ).sendDeliveryTest(payload);
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async saveReporting(companyId, payload) {
        this.savingAsset = true;
        this.error = null;
        try {
            const delivery = await services.Companies.awarePhishService(
                companyId
            ).saveReporting(payload);
            runInAction(() => {
                this.delivery = delivery;
            });
            return delivery;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async testReporting(companyId) {
        this.savingAsset = true;
        this.error = null;
        try {
            return await services.Companies.awarePhishService(
                companyId
            ).testReporting();
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async loadDeliveryProfiles(companyId) {
        this.loadingDeliveryProfiles = true;
        this.error = null;
        try {
            const result = await services.Companies.awarePhishService(
                companyId
            ).listDeliveryProfiles();
            runInAction(() => {
                this.deliveryProfiles = (result && result.profiles) || [];
            });
            return this.deliveryProfiles;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.loadingDeliveryProfiles = false;
            });
        }
    }

    async saveDeliveryProfile(companyId, profileId, payload) {
        this.savingAsset = true;
        this.error = null;
        try {
            const svc = services.Companies.awarePhishService(companyId);
            const profile = profileId
                ? await svc.updateDeliveryProfile(profileId, payload)
                : await svc.createDeliveryProfile(payload);
            await this.loadDeliveryProfiles(companyId);
            return profile;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async removeDeliveryProfile(companyId, profileId) {
        this.savingAsset = true;
        this.error = null;
        try {
            await services.Companies.awarePhishService(
                companyId
            ).deleteDeliveryProfile(profileId);
            await this.loadDeliveryProfiles(companyId);
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async generateTemplate(companyId, payload) {
        this.generatingTemplate = true;
        this.error = null;
        try {
            return await services.Companies.awarePhishService(
                companyId
            ).generateTemplate(payload);
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.generatingTemplate = false;
            });
        }
    }

    async removeTemplate(companyId, templateId) {
        this.savingAsset = true;
        this.error = null;
        try {
            await services.Companies.awarePhishService(companyId).deleteTemplate(
                templateId
            );
            await this.loadTemplates(companyId);
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async saveSendingProfile(companyId, profileId, payload) {
        this.savingAsset = true;
        this.error = null;
        try {
            const svc = services.Companies.awarePhishService(companyId);
            const p = profileId
                ? await svc.updateSendingProfile(profileId, payload)
                : await svc.createSendingProfile(payload);
            await this.loadSendingProfiles(companyId);
            return p;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }

    async removeSendingProfile(companyId, profileId) {
        this.savingAsset = true;
        this.error = null;
        try {
            await services.Companies.awarePhishService(
                companyId
            ).deleteSendingProfile(profileId);
            await this.loadSendingProfiles(companyId);
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.savingAsset = false;
            });
        }
    }
}

export default AwarePhishStore;
